import { ICache } from "@auth0/auth0-spa-js";

// auth0-spa-js namespaces every cache entry it owns under this prefix. It is not
// exported from the package root, so it is mirrored here. The id-token entry uses
// the same prefix with an "@@user@@" suffix, so this covers both.
const CACHE_KEY_PREFIX = "@@auth0spajs@@";

/**
 * Backs the Auth0 token cache with sessionStorage so tokens survive a page reload
 * while still being discarded when the tab closes.
 *
 * The default "memory" cache loses the refresh token on every reload, which forces
 * the SDK to fall back to silent auth against the shared Auth0 tenant session. When
 * another app on that tenant logs out, that session is gone and the user is bounced
 * to login. Keeping the refresh token here lets the SDK renew directly at the token
 * endpoint instead, which does not depend on the tenant session.
 *
 * Every storage access is guarded: in a partitioned iframe or a private window the
 * accessor can throw outright, and the app must degrade to an empty cache rather
 * than crash.
 */
export class SessionStorageCache implements ICache {
  set<T>(key: string, entry: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch {
      // Storage unavailable — behave as the in-memory cache would after a reload.
    }
  }

  get<T>(key: string): T | undefined {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) {
        return undefined;
      }
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  }

  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }

  allKeys(): string[] {
    try {
      return Object.keys(sessionStorage).filter((key) => key.startsWith(CACHE_KEY_PREFIX));
    } catch {
      return [];
    }
  }
}
